import moment from "moment-timezone";
export function getCurrentTime() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

export function getFirstLater(username) {
  return username.charAt(0).toUpperCase();
}

export function getRandomDarkColor() {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 31) + 70;
  const lightness = Math.floor(Math.random() * 21) + 20;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export const toAbsoluteUrl = (pathname) =>
  process.env.PUBLIC_URL + "/" + pathname;

export const getInitials = (user) =>
  `${user.fname.charAt(0)}${user.lname.charAt(0)}`;

export const parseQuestionAnswers = (data) => {
  return (
    data["Question-Answers"].map((qa) => ({
      question: qa.question,
      answers: JSON.parse(qa.answers).map((a) => a.value),
    })) || []
  );
};
export function getBrowserTimeZone() {
  return moment.tz.guess();
}

export function convertToLocalTime(
  utcDateTime,
  format = "YYYY-MM-DD HH:mm:ss"
) {
  const timeZone = getBrowserTimeZone();
  return moment.utc(utcDateTime).tz(timeZone).format(format);
}

export const extractQuestion = (text) => {
  if (!text) return "";

  // extract after colon if present
  if (text.includes(":")) {
    text = text.split(":").pop();
  }

  // extract last question sentence if ends with ?
  const match = text.match(/([^?]+)\?/);
  if (match) return match[1].trim();

  return text.trim();
};

// Small Levenshtein helper for per-word typo tolerance
const wordDistance = (a, b) => {
  const dp = Array(a.length + 1)
    .fill(null)
    .map(() => Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[a.length][b.length];
};

const wordsRoughlyEqual = (w1, w2) => {
  if (w1 === w2) return true;
  const maxLen = Math.max(w1.length, w2.length);
  if (maxLen === 0) return false;
  const dist = wordDistance(w1, w2);
  const ratio = dist / maxLen;
  // allow small typos like "aready" vs "ready"
  return ratio <= 0.25;
};

const STOPWORDS = new Set([
  "could",
  "would",
  "please",
  "tell",
  "about",
  "more",
  "like",
  "just",
  "really",
  "kind",
  "that",
  "this",
  "with",
  "have",
  "what",
  "when",
  "where",
  "which",
  "from",
  "some",
  "then",
  "lets",
  "let",
  "move",
  "onto",
  "now",
  "okay",
  "okie",
  "alright",
  "so",
  "well",
  "you",
  "your",
]);

const normalizeTokens = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));

export const isSimilarQuestion = (botQ, templateQ) => {
  if (!botQ || !templateQ) return false;

  // 1) get the actual question part from the bot
  const coreBot = extractQuestion(botQ);

  const botTokens = normalizeTokens(coreBot);
  const tmplTokens = normalizeTokens(templateQ);

  if (botTokens.length === 0 || tmplTokens.length === 0) return false;

  // 2) count how many template words we can "roughly" find in the bot question
  let shared = 0;
  const usedBotIdx = new Set();

  tmplTokens.forEach((t) => {
    for (let i = 0; i < botTokens.length; i++) {
      if (usedBotIdx.has(i)) continue;
      if (wordsRoughlyEqual(botTokens[i], t)) {
        shared++;
        usedBotIdx.add(i);
        break;
      }
    }
  });

  if (shared === 0) return false;

  // 3) how much of the template is covered?
  const coverage = shared / tmplTokens.length;

  // Require at least:
  // - 2 matching important words, AND
  // - 60% of template's important words present
  return shared >= 2 && coverage >= 0.6;
};
